import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

// ============================================================
// CAPA DE DIAGNOSTICO
// ------------------------------------------------------------
// Los eventos de embudo (PageView, ViewContent, AffiliateClick) te
// dicen SI convirtio. Estos te dicen POR QUE no.
//
// Sin postback de Kovo, estas senales son la unica forma de
// distinguir entre "el creativo no encajo" y "la oferta esta mal
// elegida". Son problemas distintos con soluciones distintas.
//
// Todos son eventos CUSTOM (trackCustom), no estandar: no compiten
// con la conversion principal por la priorizacion de AEM ni ensucian
// la optimizacion.
// ============================================================

type Fbq = (...args: any[]) => void;
const fire = (name: string, params: Record<string, any> = {}) => {
  const w = window as any;
  const fbq: Fbq | undefined = w.fbq;
  if (typeof fbq !== 'function') return;
  fbq('trackCustom', name, params);
};

// Profundidad de scroll a 25 / 50 / 75 / 100 %
const SCROLL_MARKS = [25, 50, 75, 100];
// Tiempo en pagina a 30s y 60s. Mas alla de eso no aporta.
const TIME_MARKS = [30, 60];

// Paginas que alguien visita cuando esta verificando si puede confiar.
const CREDIBILITY_PATHS = ['/metodologia', '/estandares-editoriales'];

// Mapa host -> red social. Se usa para etiquetar la salida.
const SOCIAL_HOSTS: Array<[RegExp, string]> = [[/(^|\.)youtube\.com$|(^|\.)youtu\.be$/i, 'YouTube'], [/(^|\.)instagram\.com$/i, 'Instagram'], [/(^|\.)facebook\.com$|(^|\.)fb\.com$/i, 'Facebook'], [/(^|\.)tiktok\.com$/i, 'TikTok'], [/(^|\.)x\.com$|(^|\.)twitter\.com$/i, 'X'], [/(^|\.)linkedin\.com$/i, 'LinkedIn'], [/(^|\.)threads\.net$/i, 'Threads']];
const socialNetworkFor = (href: string): string | null => {
  let host = '';
  try {
    host = new URL(href, window.location.origin).hostname;
  } catch {
    return null;
  }
  if (host === window.location.hostname) return null;
  for (const [re, name] of SOCIAL_HOSTS) {
    if (re.test(host)) return name;
  }
  return null;
};

// Devuelve solo la ruta, sin query ni hash. Los links internos ahora
// arrastran los parametros de la campana, asi que comparar el href
// completo ya no sirve.
const pathOnly = (href: string): string => {
  try {
    return new URL(href, window.location.origin).pathname.replace(/\/+$/, '');
  } catch {
    return href.split('?')[0].split('#')[0].replace(/\/+$/, '');
  }
};
export function Engagement() {
  const {
    pathname
  } = useLocation();
  const firedScroll = useRef<Set<number>>(new Set());
  const firedTime = useRef<Set<number>>(new Set());
  const maxScroll = useRef(0);

  // Reinicia los marcadores en cada cambio de ruta
  useEffect(() => {
    firedScroll.current = new Set();
    firedTime.current = new Set();
    maxScroll.current = 0;
  }, [pathname]);

  // --- Profundidad de scroll ---
  useEffect(() => {
    const onScroll = () => {
      const doc = document.documentElement;
      const alcanzable = doc.scrollHeight - window.innerHeight;
      if (alcanzable <= 0) return;
      const pct = Math.round(window.scrollY / alcanzable * 100);
      if (pct <= maxScroll.current) return;
      maxScroll.current = pct;
      SCROLL_MARKS.forEach(mark => {
        if (pct >= mark && !firedScroll.current.has(mark)) {
          firedScroll.current.add(mark);
          fire('ScrollDepth', {
            percent: mark,
            path: pathname
          });
        }
      });
    };
    window.addEventListener('scroll', onScroll, {
      passive: true
    });
    return () => window.removeEventListener('scroll', onScroll);
  }, [pathname]);

  // --- Tiempo en pagina ---
  useEffect(() => {
    const timers = TIME_MARKS.map(secs => window.setTimeout(() => {
      if (document.visibilityState !== 'visible') return;
      if (firedTime.current.has(secs)) return;
      firedTime.current.add(secs);
      fire('TimeOnPage', {
        seconds: secs,
        path: pathname
      });
    }, secs * 1000));
    return () => timers.forEach(t => window.clearTimeout(t));
  }, [pathname]);

  // --- Apertura de FAQ ---
  // Quien abre "que pasa si cancelo a los dos meses" esta en
  // consideracion profunda, con una objecion concreta. Es la mejor
  // audiencia de retargeting que produce la pagina.
  useEffect(() => {
    const onToggle = (e: Event) => {
      const el = e.target as HTMLDetailsElement;
      if (!el || el.tagName !== 'DETAILS' || !el.open) return;
      const pregunta = el.querySelector('summary')?.textContent?.trim().slice(0, 90) || '';
      fire('ViewFAQ', {
        question: pregunta,
        path: pathname
      });
    };
    // 'toggle' no burbujea: hay que capturarlo
    document.addEventListener('toggle', onToggle, true);
    return () => document.removeEventListener('toggle', onToggle, true);
  }, [pathname]);

  // --- Clicks de salida (delegado, un solo listener) ---
  // 1) CheckedCredibility: quien va a verificar como evaluamos es
  //    alguien buscando razones para confiar. Intencion, no rebote.
  // 2) SocialClick: salida a una red social. Alimenta audiencias de
  //    retargeting y dice que red pide la gente que ya esta en la
  //    pagina.
  // El click al afiliado NO se dispara aqui: lo hace pages/Go.tsx,
  // con el nombre AffiliateClick.
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      const a = (e.target as HTMLElement)?.closest?.('a');
      if (!a) return;
      const href = a.getAttribute('href') || '';
      if (!href) return;
      const ruta = pathOnly(href);
      if (CREDIBILITY_PATHS.includes(ruta)) {
        fire('CheckedCredibility', {
          destination: ruta,
          path: pathname
        });
        return;
      }
      const network = socialNetworkFor(href);
      if (network) {
        const zona = a.closest('footer') ? 'footer' : a.closest('header, nav') ? 'header' : 'body';
        fire('SocialClick', {
          network,
          placement: zona,
          path: pathname,
          destination: href
        });
      }
    };
    document.addEventListener('click', onClick);
    return () => document.removeEventListener('click', onClick);
  }, [pathname]);
  return null;
}